import { useEffect, useState } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { DEFAULT_SITE_SETTINGS } from "@/lib/siteSettings";

const ROTATE_MS = 5000;
const TRANSITION_MS = 600;

export function AnnouncementBar() {
  const { data: settings } = useSiteSettings();

  const messages = (settings?.announcements?.length
    ? settings.announcements
    : [settings?.announcement_text ?? ""]
  ).filter((m) => m && m.trim().length > 0);

  const [index, setIndex] = useState(0);
  const [outgoing, setOutgoing] = useState<string | null>(null);

  useEffect(() => {
    setIndex(0);
    if (messages.length < 2) return;
    const id = window.setInterval(() => {
      setIndex((i) => {
        setOutgoing(messages[i % messages.length]);
        return (i + 1) % messages.length;
      });
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [messages.length, messages.join("|")]);

  // Clear the outgoing message once the slide-out finishes.
  useEffect(() => {
    if (!outgoing) return;
    const id = window.setTimeout(() => setOutgoing(null), TRANSITION_MS);
    return () => window.clearTimeout(id);
  }, [outgoing]);

  if (!settings?.announcement_enabled || messages.length === 0) return null;

  const bg = settings.announcement_bg_color ?? DEFAULT_SITE_SETTINGS.announcement_bg_color;
  const color = settings.announcement_text_color ?? DEFAULT_SITE_SETTINGS.announcement_text_color;

  const textStyle = { color };
  const textClass =
    "font-ui text-xs font-semibold uppercase tracking-[0.2em] text-center px-6";

  return (
    <div
      className="relative w-full overflow-hidden py-2"
      style={{ backgroundColor: bg }}
    >
      {/* outgoing — slides all the way off the left edge */}
      {outgoing && (
        <p
          className={`absolute inset-0 flex items-center justify-center ${textClass}`}
          style={{
            ...textStyle,
            animation: `announce-exit-left ${TRANSITION_MS}ms ease-in forwards`,
          }}
        >
          {outgoing}
        </p>
      )}

      {/* incoming — arrives from the right and settles in the middle */}
      <p
        key={index}
        className={`relative flex items-center justify-center ${textClass}`}
        style={{
          ...textStyle,
          animation: `announce-enter-right ${TRANSITION_MS}ms ease-out`,
        }}
      >
        {messages[index % messages.length]}
      </p>
    </div>
  );
}
